import { useMemo } from "react";
import type { SurveyQuestion } from "@/types/survey";

export interface SurveyPage {
  pageNumber: number;
  questions: SurveyQuestion[];
}

export interface SurveyPagination {
  /** Sorted, deduplicated list of page numbers that actually contain at
   *  least one question. Always non-empty (defaults to `[1]`). */
  orderedPages: number[];
  /** Number of distinct pages — i.e. `orderedPages.length`. */
  totalPages: number;
  /** Pages with their questions, in order. Suitable for stacked layouts
   *  (editor canvas) and for indexing into during paginated navigation
   *  (public survey). */
  pages: SurveyPage[];
}

/** Derive the pagination model used by both the public respondent page
 *  and the edit builder.
 *
 *  The previous editor implementation iterated `1..max(page)` which
 *  surfaces empty page slots when authors skip page numbers (e.g. only
 *  pages 1 and 3 exist after a deletion). The public page already
 *  collapsed to distinct ordered pages — this hook makes both surfaces
 *  agree on that behavior. */
export function useSurveyPagination(
  orderedQuestions: SurveyQuestion[],
): SurveyPagination {
  return useMemo(() => {
    const set = new Set<number>();
    for (const q of orderedQuestions) {
      const p = (q as { page?: number }).page ?? 1;
      if (typeof p === "number" && p >= 1) set.add(p);
    }
    if (set.size === 0) set.add(1);
    const orderedPages = Array.from(set).sort((a, b) => a - b);
    const pages: SurveyPage[] = orderedPages.map((pageNumber) => ({
      pageNumber,
      questions: orderedQuestions.filter(
        (q) => ((q as { page?: number }).page ?? 1) === pageNumber,
      ),
    }));
    return {
      orderedPages,
      totalPages: orderedPages.length,
      pages,
    };
  }, [orderedQuestions]);
}
